// Tax Reserve Wallet — Super Admin only (freedomanil@gmail.com).
// Shows admin_tax system-wallet balance + tax collection ledger.
import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Landmark } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import api from '../../utils/api';

export default function AdminTaxWallet() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      // Ledger filtered to tax type
      const r = await api.get(`/admin/marketplace/unified-ledger?types=marketplace_tax&days=${days}&limit=200`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setBusy(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const taxWallet = (data?.system_wallets || []).find((w) => w.key === 'admin_tax') || {};
  const rows = data?.transactions || [];
  const totalCollected = rows.filter((t) => t.direction === 'credit').reduce((s, t) => s + Number(t.amount || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-950 text-white p-4 sm:p-6" data-testid="admin-tax-wallet-page">
      <Toaster position="top-right" richColors />
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent flex items-center gap-2">
              <Landmark size={28} /> Tax Reserve Wallet
            </h1>
            <div className="text-[11px] opacity-70">Super-Admin only · Accumulates 2% of every settlement</div>
          </div>
          <div className="flex gap-1">
            {[7, 30, 90, 365].map((d) => (
              <button key={d} onClick={() => setDays(d)} data-testid={`tax-days-${d}`}
                      className={`px-2 py-1 rounded text-[10px] font-black ${days === d ? 'bg-emerald-500 text-slate-900' : 'bg-slate-800 text-slate-300'}`}>{d}d</button>
            ))}
            <button onClick={load} data-testid="tax-refresh" className="p-1.5 rounded bg-slate-800">
              <RefreshCw size={12} className={busy ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 p-4 text-slate-900 shadow-xl" data-testid="tax-balance-card">
            <div className="text-[10px] font-black uppercase opacity-70">Current Balance</div>
            <div className="text-4xl font-black">{Number(taxWallet.balance || 0).toFixed(0)}<span className="text-sm"> AT</span></div>
            <div className="text-[10px] opacity-70 mt-1">Updated: {taxWallet.updated_at ? new Date(taxWallet.updated_at).toLocaleString('en-IN') : '—'}</div>
          </div>
          <div className="rounded-2xl bg-slate-900/70 border border-slate-800 p-4" data-testid="tax-collected-card">
            <div className="text-[10px] font-black uppercase opacity-70">Collected · {days}d</div>
            <div className="text-3xl font-black text-yellow-300">{totalCollected.toFixed(0)}<span className="text-sm"> AT</span></div>
            <div className="text-[10px] opacity-70">from {rows.filter((t) => t.direction === 'credit').length} tx</div>
          </div>
          <div className="rounded-2xl bg-slate-900/70 border border-slate-800 p-4" data-testid="tax-reversed-card">
            <div className="text-[10px] font-black uppercase opacity-70">Reversed · {days}d</div>
            <div className="text-3xl font-black text-rose-300">
              {rows.filter((t) => t.direction === 'debit').reduce((s, t) => s + Number(t.amount || 0), 0).toFixed(0)}<span className="text-sm"> AT</span>
            </div>
            <div className="text-[10px] opacity-70">refunds / rollbacks</div>
          </div>
        </div>

        <div className="rounded-2xl bg-slate-900/70 border border-slate-800 p-3" data-testid="tax-ledger">
          <div className="text-xs font-black text-emerald-300 uppercase mb-2">📋 Tax Ledger · Last {days} days</div>
          <div className="max-h-[520px] overflow-y-auto space-y-1">
            {rows.length === 0 && <div className="text-center opacity-60 py-6 text-xs">No tax transactions in this range</div>}
            {rows.map((tx) => (
              <div key={tx.id} className="flex items-center gap-2 text-[11px] rounded bg-slate-800/40 p-1.5" data-testid={`tax-tx-${tx.id.slice(0,8)}`}>
                <div className={`w-1.5 h-1.5 rounded-full ${tx.direction === 'credit' ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-black truncate">{tx.description || '—'}</div>
                  <div className="opacity-50 text-[9px]">order #{(tx.reference_id || '').slice(0, 8)} · {(tx.created_at || '').slice(0, 16).replace('T', ' ')}</div>
                </div>
                <div className={`font-black ${tx.direction === 'credit' ? 'text-emerald-300' : 'text-rose-300'}`}>
                  {tx.direction === 'credit' ? '+' : '−'}{Number(tx.amount).toFixed(2)} AT
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
